import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {FlagFilled} from '@ant-design/icons';
import {Button, Card, Col, Descriptions, Grid, Row, Space, theme, Typography} from 'antd';
import {AttachmentsDropzone} from 'components/AttachmentsDropzone';
import {Shell} from 'components/Shell';
import {TaskContainer} from 'components/TaskContainer';
import {checkIsAllDay, getIsMobile, getIsTablet, renderDate} from 'helpers/utils';
import {observer} from 'mobx-react-lite';
import {AppRoutePaths} from 'routing/Routes';
import {ServiceState} from 'store/services/ServiceState';
import {CardTitle, DescriptionLabel} from '../utils/ServicesUtils';

const {Text} = Typography;

export const ServiceDisplayView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {serviceId} = useParams();
  const [isAllDay, setIsAllDay] = useState(false);

  const [state] = useState(() => new ServiceState());

  const {token} = theme.useToken();
  const screen = Grid.useBreakpoint();

  const isMobile = useMemo(() => getIsMobile(screen), [screen]);
  const isTablet = useMemo(() => getIsTablet(screen), [screen]);

  useEffect(() => state.loadService(Number(serviceId)), [serviceId, state]);
  useEffect(
    () => setIsAllDay(checkIsAllDay(state.item.startDateTime, state.item.endDateTime)),
    [state.item.startDateTime, state.item.endDateTime]
  );

  return (
    <Shell
      backLink
      title={
        <>
          {state.item.name} {state.item.required && <FlagFilled style={{color: token.red}} />}
        </>
      }
      actionButtons={[
        <Button
          key="edit"
          size="large"
          className="shell-btn"
          onClick={() => navigate(`${AppRoutePaths.services.url}/${serviceId}/edit`)}
        >
          {t('Edit Service')}
        </Button>,
        <Button
          key="addTask"
          size="large"
          type="primary"
          className="shell-btn"
          onClick={() => navigate(`${AppRoutePaths.services.url}/${serviceId}/addtask`)}
        >
          {t('Add task')}
        </Button>,
      ]}
    >
      <Row gutter={16}>
        <Col className="flex flex-col gap-4" span={isMobile ? 24 : 10}>
          <Card title={<CardTitle text={t('Service')} />}>
            <Descriptions column={isTablet ? 1 : 2} colon={false} layout="vertical">
              <Descriptions.Item label={<DescriptionLabel text={t('Start date')} />}>
                {renderDate(isAllDay, state.item.startDateTime)}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('End date')} />}>
                {renderDate(isAllDay, state.item.endDateTime)}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Type')} />}>
                {state.item.serviceType?.name}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('State')} />}>
                {t('Completed ')}
                {state.item.tasks?.filter(({status}) => status === 'Completed').length}/{state.item.tasks?.length}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Description')} />} span={isTablet ? 1 : 2}>
                {state.item.description}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title={<CardTitle text={t('Attachments')} />} className="relative">
            <AttachmentsDropzone
              attachments={state.item.attachments ?? []}
              addAttachments={(files) => state.addAttachments(files)}
              removeAttachment={(id) => state.removeAttachment(id)}
              isLoading={state.isLoading}
            />
          </Card>
        </Col>
        <Col span={isMobile ? 24 : 14} className="mt-4 md:mt-0">
          <Card title={<CardTitle text={t('Tasks')} />} className="h-full">
            {state.item.tasks?.length ? (
              <Space direction="vertical" className="w-full">
                {state.item.tasks?.map((item) => (
                  <TaskContainer key={item.id} task={item} />
                ))}
              </Space>
            ) : (
              <Text italic>{t('No tasks')}</Text>
            )}
          </Card>
        </Col>
      </Row>
    </Shell>
  );
});
